// src/screens/EventEvidenceScreen.js
// Enhanced Evidence Viewer with username search and live-frames support
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View, Text, TouchableOpacity, ActivityIndicator,
  StyleSheet, SafeAreaView, ScrollView, TextInput,
  Image, Modal, FlatList
} from 'react-native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, collection, query, where, orderBy, limit, getDocs, doc, getDoc } from 'firebase/firestore';
import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { PAYOUT_STRUCTURES } from '../constants/payoutStructures';

const COLORS = {
  GOLD: '#FCD000',
  BG: '#0b0b0b',
  CARD: '#101010',
  TEXT: '#EDEDED',
  DIM: '#A0A0A0',
  BORDER: '#1a1a1a',
  SUCCESS: '#2ECC71',
  DANGER: '#E74C3C',
  WARN: '#F5A623',
  BLUE: '#3498DB',
  PURPLE: '#9B59B6'
};

// ============================================================================
// PER-GAME RESOLVER CONFIG (founder-approved mockup, 2026-07-06)
//
// The Resolve tab reads the event's game and renders the matching variant:
// identity service tag on winner buttons, and score semantics. Games not
// listed fall back to the generic points resolver. Fighting games share the
// SF6 set-score picker. GT7 splits: h2h → exit-tag resolver (no scores),
// racing mode → finishing-order panel (adminResolveRace).
// ============================================================================
const RESOLVER_GAMES = {
  mdn: { service: 'EA ID', score: 'points', scoreLabel: 'Final score — points' },
  cod: { service: 'ACTIVISION ID', score: 'points' },   // label derived from CDL mode
  gt7: { service: 'PSN ID', score: 'none' },            // H2H races have no meaningful score
  sf6: { service: 'CFN', score: 'set' },
  tk8: { service: 'TEKKEN ID', score: 'set' },
  mk1: { service: 'IGN', score: 'set' },
  ssb: { service: 'NINTENDO', score: 'set' },
  mtf: { service: 'IGN', score: 'set' },
  '2xk': { service: 'RIOT ID', score: 'set' },
  mvr: { service: 'PLATFORM ID', score: 'points' },     // label derived from mode
};
const GAME_ID_ALIASES = {
  madden: 'mdn', 'call of duty': 'cod', callofduty: 'cod',
  'gran turismo 7': 'gt7', granturismo7: 'gt7',
  'street fighter 6': 'sf6', streetfighter6: 'sf6',
  'marvel rivals': 'mvr', marvelrivals: 'mvr',
};
const normalizeGameId = (eventData) => {
  const raw = String(eventData?.game || eventData?.gameId || '').toLowerCase().trim();
  return RESOLVER_GAMES[raw] ? raw : (GAME_ID_ALIASES[raw] || raw);
};

// Score-input semantics for the selected event (label/hint/picker mode).
const scoreUiFor = (gameKey, eventData) => {
  const cfg = RESOLVER_GAMES[gameKey];
  const fmt = String(eventData?.format || eventData?.gameFormat || eventData?.eventFormat || '');
  if (!cfg) return { kind: 'points', label: 'Final score (optional, winner first)', hint: null };
  if (cfg.score === 'none') return { kind: 'none' };
  if (cfg.score === 'set') {
    const firstTo = /best of 5/i.test(fmt) ? 3 : 2;
    return { kind: 'set', firstTo, label: `Set score — games won (winner needs ${firstTo})` };
  }
  if (gameKey === 'cod') {
    const snd = /search/i.test(fmt);
    return {
      kind: 'points',
      label: snd ? 'Final score — rounds (first to 6)' : 'Final score — points',
      hint: snd ? 'Search & Destroy plays to 6 rounds.' : (/hardpoint/i.test(fmt) ? 'Hardpoint plays to 250.' : null),
    };
  }
  if (gameKey === 'mvr') {
    return {
      kind: 'points',
      label: fmt ? `Final score — ${fmt}` : 'Final score — maps/points',
      hint: 'Score unit follows the mode; per-map detail goes in the reason.',
    };
  }
  return { kind: 'points', label: cfg.scoreLabel || 'Final score (optional, winner first)', hint: null };
};

// Warzone BR payout structures - LOCAL mirror of the paid rank spans in
// functions/constants/payoutStructures.js wz-* entries (the frontend
// PAYOUT_STRUCTURES constants file doesn't carry them; it's checked first so
// a future sync wins). Only the rank spans matter here - they drive the gold
// PAYOUT highlight in the race panel. wz-solos pays ranks 1..10; wz-squads
// pays SQUAD ranks 1..5.
const WZ_PAYOUT_STRUCTURES = {
  'wz-solos-top-10': { bands: Array.from({ length: 10 }, (_, i) => ({ minRank: i + 1, maxRank: i + 1 })) },
  'wz-squads-top-5': { bands: Array.from({ length: 5 }, (_, i) => ({ minRank: i + 1, maxRank: i + 1 })) },
};

// Ranks the event's payout structure actually pays (best-effort, for the
// gold highlight in the race panel — server recomputes authoritatively).
const computePayingRanks = (payoutStructure) => {
  const ranks = new Set();
  (payoutStructure?.bands || []).forEach((b) => {
    if (Array.isArray(b.subBands) && b.subBands.length) {
      b.subBands.forEach((sb) => {
        const places = Array.isArray(sb.places) ? sb.places : [];
        if (places.length) {
          const mn = Math.min(...places), mx = Math.max(...places);
          for (let r = mn; r <= mx; r++) ranks.add(r);
        }
      });
    } else if (b.minRank) {
      for (let r = b.minRank; r <= (b.maxRank || b.minRank); r++) ranks.add(r);
    }
  });
  return ranks;
};

// One roster member (or the team's stable id) stands for the team when
// resolving — the backend normalizes to teamId/pickupGroupId (founder, 2026-07-06).
const sideWinnerId = (p) =>
  p?.teamId || p?.pickupGroupId || p?.userId || p?.roster?.[0]?.userId || null;

export default function EventEvidenceScreen({ navigation, route }) {
  const { user, isAdmin, isLoading } = useAdminAuth();
  
  // Search state
  const [searchType, setSearchType] = useState('eventId'); // 'eventId' or 'username'
  const [searchValue, setSearchValue] = useState(route.params?.eventId || route.params?.matchId || '');
  
  // Results state
  const [evidence, setEvidence] = useState(null);
  const [userEvents, setUserEvents] = useState([]);
  const [liveFrames, setLiveFrames] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [hiddenRecordings, setHiddenRecordings] = useState(0);
  const [matches, setMatches] = useState([]);

  // Admin match-resolution panel state
  const [resolveMatchId, setResolveMatchId] = useState(null);
  const [resolveWinner, setResolveWinner] = useState(null);
  const [resolveWinnerScore, setResolveWinnerScore] = useState('');
  const [resolveLoserScore, setResolveLoserScore] = useState('');
  const [resolveReason, setResolveReason] = useState('');
  const [resolveConfirm, setResolveConfirm] = useState('');
  const [resolveForce, setResolveForce] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [resolveResult, setResolveResult] = useState(null);
  // Per-game resolver state
  const [setScorePick, setSetScorePick] = useState(null);     // 'F-L' string or 'other'
  const [gt7ExitTag, setGt7ExitTag] = useState(null);         // selected loser exit-type chip
  const [racePositions, setRacePositions] = useState({});     // uid -> position text (race panel)
  const [raceDnf, setRaceDnf] = useState({});                 // uid -> bool (race panel)
  const [raceTimes, setRaceTimes] = useState({});             // uid -> elapsed-time text (heat panel)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFrame, setSelectedFrame] = useState(null);
  const [selectedRecording, setSelectedRecording] = useState(null);
  
  // Current view
  const [activeTab, setActiveTab] = useState('evidence'); // 'evidence', 'liveFrames', 'recordings'

  // ============================================================================
  // ADMIN MATCH RESOLUTION
  // ============================================================================

  const submitResolution = async () => {
    if (!evidence?.eventId || !resolveMatchId || !resolveWinner) return;
    setResolving(true);
    setResolveResult(null);
    try {
      const resolveFn = httpsCallable(getFunctions(), 'adminResolveMatch');
      const hasScores = resolveWinnerScore !== '' && resolveLoserScore !== '';
      const res = await resolveFn({
        eventId: evidence.eventId,
        matchId: resolveMatchId,
        winnerId: resolveWinner,
        winnerScore: hasScores ? Number(resolveWinnerScore) : null,
        loserScore: hasScores ? Number(resolveLoserScore) : null,
        reason: resolveReason.trim(),
        confirmText: resolveConfirm,
        force: resolveForce,
      });
      const auditId = res.data?.auditId;
      setResolveResult({
        ok: true,
        message: `✓ Match resolved — winner ${resolveWinner}${hasScores ? ` (${resolveWinnerScore}-${resolveLoserScore})` : ''}. Audit record: ${auditId}. Reloading…`,
      });
      // Reload so the match list reflects the completion
      setTimeout(() => searchByEventId(evidence.eventId), 1500);
    } catch (e) {
      setResolveResult({ ok: false, message: `Resolution rejected: ${e.message}` });
    }
    setResolving(false);
  };

  // Multi-racer events (GT7 Showdown/Heats): submit the full finishing order.
  // Completes the EVENT server-side and fires the standard payout pipeline.
  const submitRaceResolution = async () => {
    if (!evidence?.eventId) return;
    setResolving(true);
    setResolveResult(null);
    try {
      const entries = evidence.allEntries || [];
      const placements = entries.map((e) => (
        raceDnf[e.id]
          ? { userId: e.id, dnf: true }
          : { userId: e.id, position: Number(racePositions[e.id]) }
      ));
      const resolveFn = httpsCallable(getFunctions(), 'adminResolveRace');
      const res = await resolveFn({
        eventId: evidence.eventId,
        placements,
        reason: resolveReason.trim(),
        confirmText: resolveConfirm,
      });
      const d = res.data || {};
      setResolveResult({
        ok: true,
        message: `✓ Race resolved — ${d.finishers} finisher(s), ${d.dnfs} DNF. Champion: ${d.championId}. Audit: ${d.auditId}. ${d.payoutNote || ''} Reloading…`,
      });
      setTimeout(() => searchByEventId(evidence.eventId), 2000);
    } catch (e) {
      setResolveResult({ ok: false, message: `Resolution rejected: ${e.message}` });
    }
    setResolving(false);
  };

  // Multi-heat events (mode 'racing', heatsCount >= 2): resolve ONE heat.
  // "1:23.456", "83.456", or "1:23" → milliseconds (null when blank/unparseable).
  // GT7's results screen shows elapsed time for P1 and gaps for the rest —
  // enter TOTAL time either way (P1 time + gap); only comparisons matter.
  const parseRaceTime = (t) => {
    const s = String(t || '').trim();
    if (!s) return null;
    const m = s.match(/^(?:(\d+):)?(\d{1,2}(?:\.\d{1,3})?)$/);
    if (!m) return null;
    const mins = m[1] ? parseInt(m[1], 10) : 0;
    const secs = parseFloat(m[2]);
    if (!Number.isFinite(secs)) return null;
    const ms = Math.round((mins * 60 + secs) * 1000);
    return ms > 0 ? ms : null;
  };

  // Non-finals heats stamp advancers and auto-advance the round; the finals
  // heat completes the event and fires the heats-aware payout distribution.
  const submitHeatResolution = async (heat) => {
    if (!evidence?.eventId || !heat?.id) return;
    setResolving(true);
    setResolveResult(null);
    try {
      const disqualified = new Set((heat.playersDisqualified || [])
        .map((d) => (typeof d === 'string' ? d : (d?.userId || d?.odisplayName))).filter(Boolean));
      const roster = (heat.players || []).filter((p) => !disqualified.has(p.userId));
      const placements = roster.map((p) => {
        if (raceDnf[p.userId]) return { userId: p.userId, dnf: true };
        const timeMs = parseRaceTime(raceTimes[p.userId]);
        return {
          userId: p.userId,
          position: Number(racePositions[p.userId]),
          // Elapsed race time (optional): the first place BELOW the cut is
          // the one that matters — the fastest of those across the round
          // fills the open seat in the next round (full-grid pull-in).
          ...(timeMs != null ? { finishTimeMs: timeMs } : {}),
        };
      });
      const resolveFn = httpsCallable(getFunctions(), 'adminResolveHeat');
      const res = await resolveFn({
        eventId: evidence.eventId,
        matchId: heat.id,
        placements,
        reason: resolveReason.trim(),
        confirmText: resolveConfirm,
      });
      const d = res.data || {};
      setResolveResult({
        ok: true,
        message: `✓ Heat resolved (round ${d.round}${d.isFinals ? ', FINALS' : ''}) — ${d.finishers} finisher(s), ${d.dnfs} DNF${d.isFinals ? '' : `, ${(d.advancers || []).length} advance`}. Audit: ${d.auditId}. ${d.payoutNote || ''} Reloading…`,
      });
      setTimeout(() => searchByEventId(evidence.eventId), 2000);
    } catch (e) {
      setResolveResult({ ok: false, message: `Resolution rejected: ${e.message}` });
    }
    setResolving(false);
  };

  // ============================================================================
  // SEARCH FUNCTIONS
  // ============================================================================

  const searchByEventId = async (eventId) => {
    setLoading(true);
    setError(null);
    setEvidence(null);
    setLiveFrames([]);
    setRecordings([]);

    try {
      // Use the existing getMatchEvidence Cloud Function (enhanced version)
      const getEvidence = httpsCallable(getFunctions(), 'getMatchEvidence');
      const result = await getEvidence({ eventId: eventId.trim() });
      
      if (result.data.error) {
        setError(result.data.error);
        return;
      }
      
      const data = result.data;
      console.log('Evidence loaded:', data.frameCount, 'frames,', data.recordingCount, 'recordings');
      
      // Map entries to expected format
      const entriesWithVision = (data.entries || []).filter(e => e.visionOutcome);
      
      setEvidence({
        eventId: data.eventId || eventId,
        eventData: data.eventData,
        eventType: data.eventType,
        entries: entriesWithVision,
        allEntries: data.entries || [],
        streamKeys: data.streamKeys || [],
        evidence: data.evidence || [],
        tasks: data.tasks || []
      });
      
      // Set frames with signed URLs
      setLiveFrames(data.frames || []);
      
      // Store recordings for display
      setRecordings(data.recordings || []);
      setHiddenRecordings(data.hiddenRecordings || 0);
      setMatches(data.matches || []);
      // Reset the resolve panel on every fresh load
      setResolveMatchId(null);
      setResolveWinner(null);
      setResolveWinnerScore('');
      setResolveLoserScore('');
      setResolveReason('');
      setResolveConfirm('');
      setResolveForce(false);
      setResolveResult(null);
      setSetScorePick(null);
      setGt7ExitTag(null);
      setRacePositions({});
      setRaceDnf({});
      setRaceTimes({});

    } catch (err) {
      console.error('Error loading evidence:', err);
      setError(err.message || 'Failed to load evidence');
      
      // Fallback to direct Firestore query if Cloud Function fails
      console.log('Falling back to direct Firestore query...');
      await searchByEventIdFallback(eventId);
    } finally {
      setLoading(false);
    }
  };
  
  // Fallback method using direct Firestore (no frames/recordings)
  const searchByEventIdFallback = async (eventId) => {
    try {
      const db = getFirestore();
      
      let eventData = null;
      let eventType = 'match';
      
      const matchRef = doc(db, 'matches', eventId);
      const matchSnap = await getDoc(matchRef);
      
      if (matchSnap.exists()) {
        eventData = { id: matchSnap.id, ...matchSnap.data() };
      } else {
        const tournamentRef = doc(db, 'tournaments', eventId);
        const tournamentSnap = await getDoc(tournamentRef);
        
        if (tournamentSnap.exists()) {
          eventData = { id: tournamentSnap.id, ...tournamentSnap.data() };
          eventType = 'tournament';
        }
      }
      
      if (!eventData) {
        setError('Event not found');
        return;
      }
      
      // Get ALL entries
      const entriesRef = collection(db, eventType === 'match' ? 'matches' : 'tournaments', eventId, 'entries');
      const entriesSnap = await getDocs(entriesRef);
      
      const allEntries = [];
      const entriesWithVision = [];
      
      entriesSnap.forEach(doc => {
        const data = doc.data();
        const streamKey = data?.streamInfo?.streamId || data?.critcashStreamKey || null;
        
        const entry = {
          id: doc.id,
          displayName: data.displayName || data.userName || doc.id,
          streamKey,
          ...data
        };
        allEntries.push(entry);
        
        if (data.visionOutcome || data.visionFrameUrl) {
          entriesWithVision.push(entry);
        }
      });
      
      setEvidence({
        eventData,
        eventType,
        entries: entriesWithVision,
        allEntries: allEntries,
        streamKeys: allEntries.filter(e => e.streamKey).map(e => e.streamKey)
      });
      
      // Note: Can't load frames without Cloud Function due to CORS
      setError('Note: Frame/recording preview requires the enhanced getMatchEvidence function');
      
    } catch (err) {
      console.error('Fallback search error:', err);
      setError(err.message || 'Failed to load evidence');
    }
  };

  const searchByUsername = async (username) => {
    setLoading(true);
    setError(null);
    setUserEvents([]);

    try {
      const db = getFirestore();
      
      let userId = null;
      let userData = null;
      
      console.log('Searching for username:', username);
      
      // Method 1: Search public_profiles by userName (exact match)
      try {
        const publicProfilesRef = collection(db, 'public_profiles');
        const publicQuery = query(
          publicProfilesRef,
          where('userName', '==', username)
        );
        const publicSnap = await getDocs(publicQuery);
        console.log('public_profiles userName query results:', publicSnap.size);
        
        if (!publicSnap.empty) {
          userId = publicSnap.docs[0].id;
          userData = publicSnap.docs[0].data();
          console.log('Found user in public_profiles:', userId);
        }
      } catch (queryErr) {
        console.warn('public_profiles query error:', queryErr.message);
      }
      
      // Method 2: Search users collection by userName
      if (!userId) {
        try {
          const usersRef = collection(db, 'users');
          const userQuery = query(
            usersRef,
            where('userName', '==', username)
          );
          const userSnap = await getDocs(userQuery);
          console.log('users userName query results:', userSnap.size);
          
          if (!userSnap.empty) {
            userId = userSnap.docs[0].id;
            userData = userSnap.docs[0].data();
            console.log('Found user in users collection:', userId);
          }
        } catch (queryErr) {
          console.warn('users userName query error:', queryErr.message);
        }
      }
      
      // Method 3: Search users by displayName
      if (!userId) {
        try {
          const usersRef = collection(db, 'users');
          const displayQuery = query(
            usersRef,
            where('displayName', '==', username)
          );
          const displaySnap = await getDocs(displayQuery);
          console.log('users displayName query results:', displaySnap.size);
          
          if (!displaySnap.empty) {
            userId = displaySnap.docs[0].id;
            userData = displaySnap.docs[0].data();
          }
        } catch (queryErr) {
          console.warn('displayName query error:', queryErr.message);
        }
      }
      
      // Method 4: Direct lookup by ID in users
      if (!userId) {
        try {
          const userDoc = await getDoc(doc(db, 'users', username));
          if (userDoc.exists()) {
            userId = userDoc.id;
            userData = userDoc.data();
            console.log('Found by direct ID lookup in users');
          }
        } catch (docErr) {
          console.warn('Direct users lookup error:', docErr.message);
        }
      }
      
      // Method 5: Direct lookup by ID in public_profiles
      if (!userId) {
        try {
          const publicDoc = await getDoc(doc(db, 'public_profiles', username));
          if (publicDoc.exists()) {
            userId = publicDoc.id;
            userData = publicDoc.data();
            console.log('Found by direct ID lookup in public_profiles');
          }
        } catch (docErr) {
          console.warn('Direct public_profiles lookup error:', docErr.message);
        }
      }
      
      // Method 6: Scan public_profiles for case-insensitive match (last resort)
      if (!userId) {
        try {
          console.log('Trying case-insensitive scan of public_profiles...');
          const allProfilesSnap = await getDocs(collection(db, 'public_profiles'));
          const lowerUsername = username.toLowerCase();
          
          for (const profileDoc of allProfilesSnap.docs) {
            const profileData = profileDoc.data();
            if (profileData.userName && profileData.userName.toLowerCase() === lowerUsername) {
              userId = profileDoc.id;
              userData = profileData;
              console.log('Found via case-insensitive scan:', userId);
              break;
            }
          }
        } catch (scanErr) {
          console.warn('Scan error:', scanErr.message);
        }
      }
      
      if (!userId) {
        setError('User not found');
        return;
      }
      
      console.log('Found user, searching for matches with userId:', userId);
      
      const events = [];
      
      // Method: Get recent matches and check if user has an entry
      // This is the most reliable approach since matches don't have entrantIds array
      try {
        const recentMatchesQuery = query(
          collection(db, 'matches'),
          orderBy('createdAt', 'desc'),
          limit(100)  // Check more matches
        );
        const recentSnap = await getDocs(recentMatchesQuery);
        console.log('Checking', recentSnap.size, 'recent matches for user entries...');
        
        let checkedCount = 0;
        for (const matchDoc of recentSnap.docs) {
          // Check if user has an entry in this match
          try {
            const entryRef = doc(db, 'matches', matchDoc.id, 'entries', userId);
            const entrySnap = await getDoc(entryRef);
            
            if (entrySnap.exists()) {
              const matchData = matchDoc.data();
              const entryData = entrySnap.data();
              
              // Get stream key from entry
              const streamKey = entryData?.streamInfo?.streamId || 
                               entryData?.critcashStreamKey || 
                               null;
              
              console.log('Found match:', matchDoc.id, 'streamKey:', streamKey);
              
              events.push({
                id: matchDoc.id,
                type: 'match',
                ...matchData,
                userEntry: entryData,
                hasVisionData: !!(entryData?.visionOutcome),
                streamKey: streamKey
              });
            }
            checkedCount++;
          } catch (entryErr) {
            // Entry doesn't exist, skip
          }
        }
        console.log('Checked', checkedCount, 'matches, found', events.length, 'with user entries');
        
      } catch (queryErr) {
        console.error('Error searching matches:', queryErr);
        setError('Error searching matches: ' + queryErr.message);
      }

      // Also search tournaments for this user
      try {
        const recentTournamentsQuery = query(
          collection(db, 'tournaments'),
          orderBy('createdAt', 'desc'),
          limit(50)
        );
        const tournamentSnap = await getDocs(recentTournamentsQuery);
        console.log('Checking', tournamentSnap.size, 'recent tournaments for user entries...');

        for (const tournamentDoc of tournamentSnap.docs) {
          try {
            const entryRef = doc(db, 'tournaments', tournamentDoc.id, 'entries', userId);
            const entrySnap = await getDoc(entryRef);

            if (entrySnap.exists()) {
              const tournamentData = tournamentDoc.data();
              const entryData = entrySnap.data();
              const streamKey = entryData?.streamInfo?.streamId ||
                               entryData?.critcashStreamKey ||
                               null;

              events.push({
                id: tournamentDoc.id,
                type: 'tournament',
                ...tournamentData,
                userEntry: entryData,
                hasVisionData: !!(entryData?.visionOutcome),
                streamKey: streamKey
              });
            }
          } catch (entryErr) {
            // Entry doesn't exist, skip
          }
        }
      } catch (queryErr) {
        console.error('Error searching tournaments:', queryErr);
      }

      console.log('Total events found for user:', events.length);

      setUserEvents(events);
      setEvidence({ userData, userId });
      
    } catch (err) {
      console.error('Error searching by username:', err);
      setError(err.message || 'Failed to search user');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (!searchValue.trim()) {
      setError('Please enter a search value');
      return;
    }
    
    if (searchType === 'eventId') {
      searchByEventId(searchValue.trim());
    } else {
      searchByUsername(searchValue.trim());
    }
  };

  // Load evidence if eventId was passed in route params
  useEffect(() => {
    if (route.params?.eventId || route.params?.matchId) {
      const eventId = route.params?.eventId || route.params?.matchId;
      setSearchValue(eventId);
      setSearchType('eventId');
      searchByEventId(eventId);
    }
  }, [route.params?.eventId, route.params?.matchId]);

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  const renderSearchTypeToggle = () => (
    <View style={styles.toggleContainer}>
      <TouchableOpacity
        style={[styles.toggleBtn, searchType === 'eventId' && styles.toggleBtnActive]}
        onPress={() => setSearchType('eventId')}
      >
        <Text style={[styles.toggleText, searchType === 'eventId' && styles.toggleTextActive]}>
          Event ID
        </Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.toggleBtn, searchType === 'username' && styles.toggleBtnActive]}
        onPress={() => setSearchType('username')}
      >
        <Text style={[styles.toggleText, searchType === 'username' && styles.toggleTextActive]}>
          Username
        </Text>
      </TouchableOpacity>
    </View>
  );

  const renderTabBar = () => (
    <View style={styles.tabBar}>
      <TouchableOpacity
        style={[styles.tab, activeTab === 'evidence' && styles.tabActive]}
        onPress={() => setActiveTab('evidence')}
      >
        <Text style={[styles.tabText, activeTab === 'evidence' && styles.tabTextActive]}>
          📊 Evidence
        </Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.tab, activeTab === 'liveFrames' && styles.tabActive]}
        onPress={() => setActiveTab('liveFrames')}
      >
        <Text style={[styles.tabText, activeTab === 'liveFrames' && styles.tabTextActive]}>
          🖼️ Frames ({liveFrames.length})
        </Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.tab, activeTab === 'recordings' && styles.tabActive]}
        onPress={() => setActiveTab('recordings')}
      >
        <Text style={[styles.tabText, activeTab === 'recordings' && styles.tabTextActive]}>
          🎬 Recordings ({recordings.length})
        </Text>
      </TouchableOpacity>
      {(matches.length > 0 || (evidence?.eventType === 'tournament' && ['racing', 'showdown'].includes(evidence?.eventData?.mode))) && (
        <TouchableOpacity
          style={[styles.tab, activeTab === 'resolve' && styles.tabActive]}
          onPress={() => setActiveTab('resolve')}
        >
          <Text style={[styles.tabText, activeTab === 'resolve' && styles.tabTextActive]}>
            ⚖️ Resolve
          </Text>
        </TouchableOpacity>
      )}
    </View>
  );

  const renderEventCard = (event) => (
    <TouchableOpacity
      key={event.id}
      style={styles.eventCard}
      onPress={() => {
        setSearchValue(event.id);
        setSearchType('eventId');
        searchByEventId(event.id);
      }}
    >
      <View style={styles.eventCardHeader}>
        <Text style={styles.eventCardTitle}>
          {event.gameName || event.game || 'Unknown Game'}
        </Text>
        {event.hasVisionData && (
          <View style={styles.visionBadge}>
            <Text style={styles.visionBadgeText}>✓ Verified</Text>
          </View>
        )}
      </View>
      
      <Text style={styles.eventCardId} selectable>
        {event.type === 'tournament' ? '🏆 ' : '⚔️ '}{event.type?.toUpperCase()} • {event.id}
      </Text>
      
      <View style={styles.eventCardRow}>
        <Text style={styles.eventCardLabel}>Status:</Text>
        <Text style={[
          styles.eventCardValue,
          { color: event.status === 'completed' ? COLORS.SUCCESS : 
                   event.status === 'live' ? COLORS.GOLD : COLORS.DIM }
        ]}>
          {event.status?.toUpperCase()}
        </Text>
      </View>
      
      {event.userEntry?.visionOutcome && (
        <View style={styles.eventCardRow}>
          <Text style={styles.eventCardLabel}>Vision Result:</Text>
          <Text style={[
            styles.eventCardValue,
            { color: event.userEntry.visionOutcome === 'win' ? COLORS.SUCCESS : COLORS.DANGER }
          ]}>
            {event.userEntry.visionOutcome.toUpperCase()} 
            ({((event.userEntry.visionConfidence || 0) * 100).toFixed(0)}%)
          </Text>
        </View>
      )}
      
      {event.streamKey && (
        <View style={styles.eventCardRow}>
          <Text style={styles.eventCardLabel}>Stream Key:</Text>
          <Text style={[styles.eventCardValue, { color: COLORS.BLUE, fontSize: 10 }]} selectable>
            {event.streamKey}
          </Text>
        </View>
      )}
      
      <Text style={styles.eventCardDate}>
        {event.createdAt?.toDate ? event.createdAt.toDate().toLocaleString() : 'N/A'}
      </Text>
    </TouchableOpacity>
  );

  const renderEntryEvidence = (entry) => (
    <View key={entry.id} style={styles.entryCard}>
      <Text style={styles.entryTitle}>User: {entry.displayName || entry.id}</Text>
      
      <View style={styles.entryRow}>
        <Text style={styles.entryLabel}>User ID:</Text>
        <Text style={[styles.entryValue, { fontSize: 10, fontFamily: 'monospace' }]} selectable>
          {entry.id}
        </Text>
      </View>
      
      {entry.streamKey && (
        <View style={styles.entryRow}>
          <Text style={styles.entryLabel}>Stream Key:</Text>
          <Text style={[styles.entryValue, { fontSize: 10, fontFamily: 'monospace' }]} selectable>
            {entry.streamKey}
          </Text>
        </View>
      )}
      
      <View style={styles.entryRow}>
        <Text style={styles.entryLabel}>Vision Outcome:</Text>
        <Text style={[
          styles.entryValue,
          { color: entry.visionOutcome === 'win' ? COLORS.SUCCESS : 
                   entry.visionOutcome === 'loss' ? COLORS.DANGER : 
                   entry.visionOutcome === 'advances' ? COLORS.SUCCESS :
                   entry.visionOutcome === 'eliminated' ? COLORS.DANGER :
                   entry.visionOutcome === 'payout' ? COLORS.SUCCESS :
                   entry.visionOutcome === 'dnf' ? COLORS.DANGER : COLORS.DIM }
        ]}>
          {entry.visionOutcome?.toUpperCase() || 'N/A'}
        </Text>
      </View>
      
      <View style={styles.entryRow}>
        <Text style={styles.entryLabel}>Confidence:</Text>
        <Text style={styles.entryValue}>
          {((entry.visionConfidence || 0) * 100).toFixed(1)}%
        </Text>
      </View>
      
      {entry.visionPosition && (
        <View style={styles.entryRow}>
          <Text style={styles.entryLabel}>Position:</Text>
          <Text style={styles.entryValue}>P{entry.visionPosition}</Text>
        </View>
      )}
      
      <View style={styles.entryRow}>
        <Text style={styles.entryLabel}>Pattern:</Text>
        <Text style={styles.entryValue}>{entry.visionPattern || 'N/A'}</Text>
      </View>
      
      <View style={styles.entryRow}>
        <Text style={styles.entryLabel}>Method:</Text>
        <Text style={styles.entryValue}>{entry.visionAnalysisMethod || 'N/A'}</Text>
      </View>
      
      <View style={styles.entryRow}>
        <Text style={styles.entryLabel}>Game Type:</Text>
        <Text style={styles.entryValue}>{entry.visionGameType || 'N/A'}</Text>
      </View>
      
      <View style={styles.entryRow}>
        <Text style={styles.entryLabel}>Frame #:</Text>
        <Text style={styles.entryValue}>{entry.visionFrameNumber || 'N/A'}</Text>
      </View>
      
      {entry.visionDetails && (
        <View style={styles.entryRow}>
          <Text style={styles.entryLabel}>Details:</Text>
          <Text style={styles.entryValue}>{entry.visionDetails}</Text>
        </View>
      )}
      
      {entry.visionDetectedAt && (
        <View style={styles.entryRow}>
          <Text style={styles.entryLabel}>Detected:</Text>
          <Text style={styles.entryValue}>
            {entry.visionDetectedAt?.toDate ? 
              entry.visionDetectedAt.toDate().toLocaleString() : 'N/A'}
          </Text>
        </View>
      )}
      
      <View style={styles.entryRow}>
        <Text style={styles.entryLabel}>Requires Review:</Text>
        <Text style={[
          styles.entryValue,
          { color: entry.visionRequiresReview ? COLORS.GOLD : COLORS.SUCCESS }
        ]}>
          {entry.visionRequiresReview ? 'YES' : 'NO'}
        </Text>
      </View>
      
      {entry.visionFrameUrl && (
        <View style={styles.entryRow}>
          <Text style={styles.entryLabel}>Frame URL:</Text>
          <Text style={[styles.entryValue, { fontSize: 9 }]} numberOfLines={2} selectable>
            {entry.visionFrameUrl}
          </Text>
        </View>
      )}
    </View>
  );

  const renderLiveFrame = ({ item, index }) => (
    <TouchableOpacity
      style={styles.frameCard}
      onPress={() => setSelectedFrame({ frame: item, index })}
    >
      {item.url ? (
        <Image
          source={{ uri: item.url }}
          style={styles.frameThumbnail}
          resizeMode="cover"
        />
      ) : (
        <View style={[styles.frameThumbnail, styles.frameError]}>
          <Text style={styles.frameErrorText}>⚠️</Text>
          <Text style={styles.frameErrorLabel}>Load Error</Text>
        </View>
      )}
      <View style={styles.frameInfo}>
        <Text style={styles.frameFilename} numberOfLines={1}>
          {item.filename || `Frame #${index + 1}`}
        </Text>
        {item.outcome && (
          <Text style={[
            styles.frameOutcome,
            { color: item.outcome === 'win' ? COLORS.SUCCESS : 
                     item.outcome === 'loss' ? COLORS.DANGER : COLORS.DIM }
          ]}>
            {item.outcome?.toUpperCase() || ''} {item.confidence ? `(${(item.confidence * 100).toFixed(0)}%)` : ''}
          </Text>
        )}
        <Text style={styles.frameUser} numberOfLines={1}>
          {item.displayName || item.userId?.substring(0, 12) || 'Unknown'}
        </Text>
        {item.size && (
          <Text style={styles.frameSize}>{formatBytes(item.size)}</Text>
        )}
      </View>
    </TouchableOpacity>
  );
  
  // Helper to format bytes
  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  if (isLoading || !user) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.centerWrap}>
          <ActivityIndicator color={COLORS.GOLD} />
        </View>
      </SafeAreaView>
    );
  }

  if (!isAdmin) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.centerWrap}>
          <Text style={styles.deniedText}>Access Denied</Text>
          <TouchableOpacity onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))}>
            <Text style={styles.backLink}>Go Back</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <Header navigation={navigation} />
      <ScrollView contentContainerStyle={styles.container}>
        <View style={styles.header}>
          <Text style={styles.title}>🔍 Event Evidence Viewer</Text>
          <Text style={styles.subtitle}>Review vision analysis evidence and live frames</Text>
        </View>

        {/* Search Type Toggle */}
        {renderSearchTypeToggle()}

        {/* Search Bar */}
        <View style={styles.searchSection}>
          <TextInput
            style={styles.input}
            placeholder={searchType === 'eventId' ? 'Enter Event/Match ID' : 'Enter Username or User ID'}
            placeholderTextColor={COLORS.DIM}
            value={searchValue}
            onChangeText={setSearchValue}
            autoCapitalize="none"
            autoCorrect={false}
          />
          <TouchableOpacity
            style={styles.searchBtn}
            onPress={handleSearch}
            disabled={loading}
          >
            <Text style={styles.searchBtnText}>
              {loading ? 'Searching...' : 'Search'}
            </Text>
          </TouchableOpacity>
        </View>

        {/* Error Message */}
        {error && (
          <View style={styles.errorBox}>
            <Text style={styles.errorText}>❌ {error}</Text>
          </View>
        )}

        {/* Loading State */}
        {loading && (
          <View style={styles.loadingWrap}>
            <ActivityIndicator color={COLORS.GOLD} size="large" />
            <Text style={styles.loadingText}>Loading evidence...</Text>
          </View>
        )}

        {/* Username Search Results - User Events List */}
        {searchType === 'username' && userEvents.length > 0 && !loading && (
          <View style={styles.resultsSection}>
            <Text style={styles.sectionTitle}>
              Recent Events ({userEvents.length})
            </Text>
            {userEvents.map(renderEventCard)}
          </View>
        )}

        {/* Event ID Search Results */}
        {searchType === 'eventId' && evidence?.eventData && !loading && (
          <View style={styles.resultsSection}>
            {/* Tab Bar */}
            {renderTabBar()}
            
            {/* Event Info */}
            <Section title="Event Information">
              <InfoRow label="Event ID" value={evidence.eventData.id} />
              <InfoRow label="Type" value={evidence.eventType} />
              <InfoRow label="Game" value={evidence.eventData.gameName || evidence.eventData.game || 'N/A'} />
              <InfoRow label="Status" value={evidence.eventData.status?.toUpperCase() || 'N/A'} />
              <InfoRow label="Mode" value={evidence.eventData.mode || 'N/A'} />
            </Section>
            
            {/* Evidence Tab */}
            {activeTab === 'evidence' && (
              <>
                <Section title={`Entries with Vision Data (${evidence.entries?.length || 0})`}>
                  {evidence.entries && evidence.entries.length > 0 ? (
                    evidence.entries.map(renderEntryEvidence)
                  ) : (
                    <Text style={styles.noDataText}>No vision data found for any entries</Text>
                  )}
                </Section>
                
                {/* All Entries (to show stream keys for recording lookup) */}
                <Section title={`All Entries (${evidence.allEntries?.length || 0})`}>
                  {evidence.allEntries && evidence.allEntries.length > 0 ? (
                    evidence.allEntries.map(entry => (
                      <View key={entry.id} style={styles.miniEntryCard}>
                        <View style={styles.miniEntryRow}>
                          <Text style={styles.miniEntryName}>
                            {entry.displayName || entry.userName || 'Unknown'}
                          </Text>
                          <Text style={[
                            styles.miniEntryStatus,
                            { color: entry.visionOutcome ? COLORS.SUCCESS : COLORS.DIM }
                          ]}>
                            {entry.visionOutcome ? '✓ Vision' : 'No Vision'}
                          </Text>
                        </View>
                        <Text style={styles.miniEntryId} selectable>
                          ID: {entry.id}
                        </Text>
                        {entry.streamKey && (
                          <Text style={styles.miniEntryStreamKey} selectable>
                            Stream Key: {entry.streamKey}
                          </Text>
                        )}
                        {!entry.streamKey && entry.critcashStreamKey && (
                          <Text style={styles.miniEntryStreamKey} selectable>
                            Stream Key: {entry.critcashStreamKey}
                          </Text>
                        )}
                      </View>
                    ))
                  ) : (
                    <Text style={styles.noDataText}>No entries found</Text>
                  )}
                </Section>
              </>
            )}
            
            {/* Live Frames Tab */}
            {activeTab === 'liveFrames' && (
              <Section title={`Live Captured Frames (${liveFrames.length})`}>
                {liveFrames.length > 0 ? (
                  <FlatList
                    data={liveFrames}
                    renderItem={renderLiveFrame}
                    keyExtractor={(item, index) => `${item.path || item.fullPath || index}-${index}`}
                    numColumns={2}
                    scrollEnabled={false}
                    columnWrapperStyle={styles.framesRow}
                  />
                ) : (
                  <Text style={styles.noDataText}>No live frames captured for this event</Text>
                )}
              </Section>
            )}
            
            {/* Recordings Tab */}
            {activeTab === 'recordings' && (
              <Section title={`Stream Recordings (${recordings.length})`}>
                {hiddenRecordings > 0 && (
                  <Text style={[styles.noDataText, { marginBottom: 10 }]}>
                    {hiddenRecordings} recording{hiddenRecordings === 1 ? '' : 's'} from outside this event's time window hidden
                  </Text>
                )}
                {recordings.length > 0 ? (
                  <View>
                    {recordings.map((recording, index) => (
                      <TouchableOpacity
                        key={index}
                        style={styles.recordingCard}
                        onPress={() => {
                          if (recording.url) {
                            setSelectedRecording(recording);
                          }
                        }}
                      >
                        {recording.thumbnailUrl ? (
                          <Image
                            source={{ uri: recording.thumbnailUrl }}
                            style={{ width: 48, height: 36, borderRadius: 4, marginRight: 10 }}
                          />
                        ) : (
                          <View style={styles.recordingIcon}>
                            <Text style={styles.recordingIconText}>🎬</Text>
                          </View>
                        )}
                        <View style={styles.recordingInfo}>
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                            <Text style={styles.recordingFilename} numberOfLines={1}>
                              {recording.displayName || recording.filename}
                            </Text>
                            {recording.isEventClip && (
                              <View style={styles.clipBadge}>
                                <Text style={styles.clipBadgeText}>EVENT CLIP</Text>
                              </View>
                            )}
                          </View>
                          <Text style={styles.recordingMeta}>
                            {recording.source === 'mux_clip'
                              ? recording.clipStatus && recording.clipStatus !== 'ready'
                                ? `${recording.clipStatus === 'clipping' ? 'Creating clip...' : recording.clipStatus}${recording.errorMessage ? ` - ${recording.errorMessage}` : ''}`
                                : `${recording.duration ? `${Math.round(recording.duration)}s` : 'Processing'} • ${recording.created ? new Date(recording.created).toLocaleString() : ''}`
                              : recording.source === 'mux'
                                ? `Full VOD • ${recording.duration ? `${Math.round(recording.duration)}s` : 'Processing'} • ${recording.created ? new Date(recording.created).toLocaleString() : ''}`
                                : `${formatBytes(recording.size)} • ${new Date(recording.created).toLocaleString()}`
                            }
                          </Text>
                          <Text style={styles.recordingStreamKey} numberOfLines={1}>
                            {recording.source === 'mux_clip'
                              ? `Event Recording${recording.mp4Ready ? ' • MP4 Ready' : ''}`
                              : recording.source === 'mux'
                                ? `Full Stream VOD (Mux)${recording.mp4Ready ? ' • MP4 Ready' : ''}`
                                : recording.source === 'critcash-relay'
                                  ? 'CritCash Relay VOD segment'
                                  : recording.streamKey
                            }
                          </Text>
                        </View>
                        {recording.url ? (
                          <Text style={styles.recordingPlay}>▶</Text>
                        ) : (
                          <Text style={[styles.recordingPlay, { opacity: 0.3 }]}>⏳</Text>
                        )}
                      </TouchableOpacity>
                    ))}
                  </View>
                ) : (
                  <Text style={styles.noDataText}>No recordings found for this event</Text>
                )}
              </Section>
            )}

            {/* Resolve Tab — guarded admin resolution, per-game variants */}
            {activeTab === 'resolve' && (() => {
              const gameKey = normalizeGameId(evidence?.eventData);
              const resolverCfg = RESOLVER_GAMES[gameKey] || null;
              const serviceTag = resolverCfg?.service || 'IGN';
              // Finishing-order resolver covers racing heats AND single-lobby
              // showdowns (GT7 one-race, Warzone BR) - all adminResolveRace.
              const isRaceEvent = evidence?.eventType === 'tournament'
                && ['racing', 'showdown'].includes(evidence?.eventData?.mode);
              const allEntries = evidence?.allEntries || [];
              const reasonOk = resolveReason.trim().length >= 10;

              // ---- shared sub-renders ----------------------------------------

              // Identity block: game IGN + service tag + FULL userId (+ CritCash
              // name when it differs). Founder req: buttons must show both the
              // IGN and the full userId.
              const renderIdentity = (p, selected) => {
                const ign = p?.ign || null;
                const primary = ign || p?.displayName || p?.userId || '?';
                return (
                  <View>
                    <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                      <Text style={[styles.searchBtnText, { color: selected ? '#000' : COLORS.TEXT, fontSize: 15, textAlign: 'left' }]}>
                        🏆 {primary}
                      </Text>
                      {ign ? (
                        <Text style={{
                          fontSize: 9, fontWeight: '700', letterSpacing: 1,
                          color: selected ? '#05400f' : COLORS.GOLD,
                          borderWidth: 1, borderColor: selected ? '#05400f' : COLORS.GOLD,
                          borderRadius: 4, paddingHorizontal: 5, paddingVertical: 1,
                        }}>{serviceTag}</Text>
                      ) : (
                        <Text style={{
                          fontSize: 9, fontWeight: '700', letterSpacing: 1,
                          color: selected ? '#5a3d00' : COLORS.WARN,
                          borderWidth: 1, borderColor: selected ? '#5a3d00' : COLORS.WARN,
                          borderRadius: 4, paddingHorizontal: 5, paddingVertical: 1,
                        }}>NO IGN ON FILE</Text>
                      )}
                    </View>
                    {p?.userId && (
                      <Text selectable style={{
                        fontFamily: 'monospace', fontSize: 11, marginTop: 4,
                        color: selected ? '#0a4a1e' : COLORS.DIM,
                      }}>{p.userId}</Text>
                    )}
                    {ign && p?.displayName && p.displayName !== ign && (
                      <Text style={{ fontSize: 11, marginTop: 2, color: selected ? '#14532d' : COLORS.DIM }}>
                        CritCash: {p.displayName}
                      </Text>
                    )}
                  </View>
                );
              };

              // Team card body: roster with per-member IGN + full uid.
              const renderRoster = (p, selected) => (
                <View style={{
                  marginTop: 8, paddingTop: 8, gap: 4,
                  borderTopWidth: 1, borderTopColor: selected ? 'rgba(0,0,0,0.25)' : 'rgba(255,255,255,0.08)',
                }}>
                  {(p.roster || []).map((r) => (
                    <View key={r.userId} style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, alignItems: 'baseline' }}>
                      <Text style={{ fontSize: 12, fontWeight: '600', minWidth: 120, color: selected ? '#000' : COLORS.TEXT }}>
                        {r.ign || r.displayName || r.userId}
                      </Text>
                      <Text selectable style={{ fontFamily: 'monospace', fontSize: 10, color: selected ? '#0a4a1e' : COLORS.DIM }}>
                        {r.userId}
                      </Text>
                    </View>
                  ))}
                </View>
              );

              // Vision context strip: what vision concluded per participant, so
              // the admin never has to flip back to the Evidence tab.
              const renderVisionStrip = (uids) => {
                const chips = uids
                  .map((uid) => allEntries.find((e) => e.id === uid))
                  .filter(Boolean)
                  .map((e) => {
                    const label = e.ign || e.displayName || e.id;
                    const out = e.visionOutcome
                      ? `${e.visionOutcome.toUpperCase()} ${e.visionConfidence != null ? (Number(e.visionConfidence)).toFixed(2) : ''}`.trim()
                      : 'NO SIGNAL';
                    const color = e.visionOutcome === 'win' ? COLORS.SUCCESS
                      : e.visionOutcome === 'loss' ? COLORS.DANGER : COLORS.WARN;
                    return { id: e.id, label, out, color, pattern: e.visionPattern };
                  });
                if (chips.length === 0) return null;
                return (
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                    {chips.map((c) => (
                      <View key={c.id} style={{
                        borderWidth: 1, borderColor: COLORS.BORDER, borderRadius: 6,
                        paddingHorizontal: 8, paddingVertical: 4, backgroundColor: COLORS.CARD,
                      }}>
                        <Text style={{ fontSize: 11, color: COLORS.DIM }}>
                          <Text style={{ color: COLORS.TEXT, fontWeight: '600' }}>{c.label}</Text>
                          {' · vision: '}
                          <Text style={{ color: c.color, fontWeight: '700' }}>{c.out}</Text>
                          {c.pattern ? ` · ${c.pattern}` : ''}
                        </Text>
                      </View>
                    ))}
                  </View>
                );
              };

              const renderReasonConfirmSubmit = (canSubmit, onSubmit, submitLabel) => (
                <View>
                  <Text style={styles.recordingFilename}>Reason (required — goes on the audit record)</Text>
                  <TextInput
                    style={[styles.input, { marginTop: 10, minHeight: 60 }]}
                    value={resolveReason}
                    onChangeText={setResolveReason}
                    placeholder="e.g. Reviewed VOD at 15:32 — final card shows Kreger13 won 9-7"
                    placeholderTextColor={COLORS.DIM}
                    multiline
                  />
                  {!reasonOk && resolveReason.length > 0 && (
                    <Text style={[styles.errorText, { marginBottom: 8 }]}>At least 10 characters.</Text>
                  )}

                  <Text style={[styles.recordingFilename, { marginTop: 6 }]}>Type RESOLVE to confirm</Text>
                  <TextInput
                    style={[styles.input, { marginTop: 10 }]}
                    value={resolveConfirm}
                    onChangeText={setResolveConfirm}
                    placeholder="RESOLVE"
                    placeholderTextColor={COLORS.DIM}
                    autoCapitalize="characters"
                  />

                  <TouchableOpacity
                    style={[styles.searchBtn, { opacity: canSubmit ? 1 : 0.4 }]}
                    disabled={!canSubmit}
                    onPress={onSubmit}
                  >
                    <Text style={styles.searchBtnText}>
                      {resolving ? 'Resolving…' : submitLabel}
                    </Text>
                  </TouchableOpacity>

                  {resolveResult && (
                    <View style={[styles.errorBox, { marginTop: 12, backgroundColor: (resolveResult.ok ? COLORS.SUCCESS : COLORS.DANGER) + '20', borderColor: resolveResult.ok ? COLORS.SUCCESS : COLORS.DANGER }]}>
                      <Text style={[styles.errorText, { color: resolveResult.ok ? COLORS.SUCCESS : COLORS.DANGER }]}>
                        {resolveResult.message}
                      </Text>
                    </View>
                  )}
                </View>
              );

              // ---- RACE PANEL (GT7 Showdown/Heats — finishing order) ---------
              if (isRaceEvent) {
                const eventData = evidence?.eventData || {};

                // ---- HEAT PANEL (multi-heat racing: resolve ONE heat) --------
                const heatsCountNum = Math.max(
                  Math.floor(Number(eventData.heatsCount || eventData.heatCount) || 0),
                  Math.floor(Number(eventData.declaredHeatsCount) || 0));
                const isHeatsEvent = String(eventData.mode || '').toLowerCase() === 'racing' && heatsCountNum >= 2;
                if (isHeatsEvent) {
                  const heatDocs = (matches || [])
                    .filter((m) => m.matchType === 'heat'
                      || (Array.isArray(m.players) && m.players.length >= 2 && !m.p1 && !m.p2))
                    .sort((a, b) => (a.round || 0) - (b.round || 0) || (a.matchNumber || 0) - (b.matchNumber || 0));
                  const heat = heatDocs.find((m) => m.id === resolveMatchId) || null;
                  const heatDisqualified = new Set((heat?.playersDisqualified || [])
                    .map((d) => (typeof d === 'string' ? d : (d?.userId || d?.odisplayName))).filter(Boolean));
                  const heatRoster = (heat?.players || []).filter((p) => !heatDisqualified.has(p.userId));
                  const heatTerminal = !!heat && ['completed', 'cancelled'].includes(String(heat.status));
                  const heatFinisherCount = heatRoster.filter((p) => !raceDnf[p.userId]).length;
                  const heatPositions = heatRoster.filter((p) => !raceDnf[p.userId])
                    .map((p) => Number(racePositions[p.userId]));
                  const heatAllFilled = heatPositions.every((p) => Number.isInteger(p) && p >= 1);
                  const heatSorted = [...heatPositions].sort((a, b) => a - b);
                  const heatContiguous = heatAllFilled && heatSorted.every((p, i) => p === i + 1);
                  // Times stay OPTIONAL — but a TYPED time that doesn't parse
                  // must block submit, not silently drop: a typo on the
                  // first-below-cut racer would strip their pull-in
                  // eligibility with nothing but a red border.
                  const heatTimesOk = heatRoster.every((p) => raceDnf[p.userId]
                    || !(raceTimes[p.userId] || '').trim()
                    || parseRaceTime(raceTimes[p.userId]) != null);
                  const heatCanSubmit = !!heat && !heatTerminal && heatFinisherCount > 0
                    && heatContiguous && heatTimesOk && reasonOk && resolveConfirm === 'RESOLVE' && !resolving;

                  return (
                    <Section title="⚖️ Resolve Heat (Admin)">
                      <Text style={[styles.noDataText, { marginBottom: 14, textAlign: 'left' }]}>
                        Multi-heat event: pick ONE heat and set its finishing order.
                        Non-finals heats advance their top half automatically once every
                        heat in the round is settled; the finals heat completes the
                        event and runs payouts. Positions run 1…{heatFinisherCount || 'N'}
                        within the heat; non-finishers get DNF. All inputs are
                        re-validated server-side.
                      </Text>

                      {heatDocs.length === 0 && (
                        <Text style={styles.noDataText}>No heats seeded yet — the event may not be locked.</Text>
                      )}

                      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                        {heatDocs.map((m) => {
                          const selected = m.id === resolveMatchId;
                          const label = `R${m.round || 1} Heat ${m.heatNumber || m.matchNumber} · ${String(m.status || 'pending').toUpperCase()}`;
                          return (
                            <TouchableOpacity
                              key={m.id}
                              onPress={() => {
                                setResolveMatchId(selected ? null : m.id);
                                setRacePositions({});
                                setRaceDnf({});
                                setRaceTimes({});
                              }}
                              style={{
                                borderWidth: 1, borderRadius: 6, paddingHorizontal: 10, paddingVertical: 6,
                                borderColor: selected ? COLORS.GOLD : COLORS.BORDER,
                                backgroundColor: selected ? COLORS.GOLD : COLORS.CARD,
                              }}
                            >
                              <Text style={{ color: selected ? '#000' : COLORS.TEXT, fontSize: 12, fontWeight: '700' }}>
                                {label}
                              </Text>
                            </TouchableOpacity>
                          );
                        })}
                      </View>

                      {heat && heatTerminal && (
                        <View style={[styles.errorBox, { marginBottom: 12 }]}>
                          <Text style={styles.errorText}>
                            This heat is already {heat.status} — pick another heat.
                          </Text>
                        </View>
                      )}

                      {heat && !heatTerminal && heatRoster.length === 0 && (
                        <Text style={styles.noDataText}>Heat roster unavailable — reload the event.</Text>
                      )}

                      {heat && !heatTerminal && heatRoster.map((p) => {
                        const dnf = !!raceDnf[p.userId];
                        const pos = racePositions[p.userId] || '';
                        return (
                          <View key={p.userId} style={{
                            flexDirection: 'row', alignItems: 'center', gap: 10,
                            backgroundColor: COLORS.CARD, borderWidth: 1, borderRadius: 8,
                            borderColor: COLORS.BORDER, padding: 10, marginBottom: 8,
                          }}>
                            <TextInput
                              style={[styles.input, {
                                width: 58, marginBottom: 0, textAlign: 'center', opacity: dnf ? 0.3 : 1,
                                borderColor: COLORS.BORDER, color: COLORS.TEXT,
                              }]}
                              value={dnf ? '—' : pos}
                              editable={!dnf}
                              onChangeText={(t) => setRacePositions((prev) => ({ ...prev, [p.userId]: t.replace(/[^0-9]/g, '') }))}
                              placeholder="P#"
                              placeholderTextColor={COLORS.DIM}
                              keyboardType="numeric"
                            />
                            <View style={{ flex: 1 }}>
                              <Text style={{ color: COLORS.TEXT, fontSize: 14, fontWeight: '600' }}>
                                {p.ign || p.displayName || p.userId}
                              </Text>
                              <Text selectable style={{ fontFamily: 'monospace', fontSize: 10, color: COLORS.DIM }}>
                                {p.userId}
                              </Text>
                            </View>
                            <TextInput
                              style={[styles.input, {
                                width: 88, marginBottom: 0, textAlign: 'center', opacity: dnf ? 0.3 : 1,
                                borderColor: (raceTimes[p.userId] || '').trim() && parseRaceTime(raceTimes[p.userId]) == null
                                  ? COLORS.DANGER : COLORS.BORDER,
                                color: COLORS.TEXT,
                              }]}
                              value={dnf ? '—' : (raceTimes[p.userId] || '')}
                              editable={!dnf}
                              onChangeText={(t) => setRaceTimes((prev) => ({ ...prev, [p.userId]: t.replace(/[^0-9:.]/g, '') }))}
                              placeholder="m:ss.000"
                              placeholderTextColor={COLORS.DIM}
                            />
                            <TouchableOpacity
                              onPress={() => setRaceDnf((prev) => ({ ...prev, [p.userId]: !prev[p.userId] }))}
                              style={{
                                borderWidth: 1, borderRadius: 6, paddingHorizontal: 8, paddingVertical: 5,
                                borderColor: dnf ? COLORS.DANGER : COLORS.BORDER,
                                backgroundColor: dnf ? COLORS.DANGER + '30' : 'transparent',
                              }}
                            >
                              <Text style={{ color: dnf ? COLORS.DANGER : COLORS.DIM, fontSize: 11, fontWeight: '700' }}>DNF</Text>
                            </TouchableOpacity>
                          </View>
                        );
                      })}

                      {heat && !heatTerminal && heatRoster.length > 0 && (
                        <Text style={{ color: COLORS.DIM, fontSize: 11, marginBottom: 10 }}>
                          Times are optional — but the first place BELOW the cut needs one:
                          the fastest of those across the round takes the open seat in the
                          next round. Enter TOTAL elapsed time (P1 time + gap works).
                        </Text>
                      )}

                      {heat && !heatTerminal && heatRoster.length > 0
                        && renderReasonConfirmSubmit(
                          heatCanSubmit,
                          () => submitHeatResolution(heat),
                          `RESOLVE R${heat.round || 1} HEAT ${heat.heatNumber || heat.matchNumber}`,
                        )}
                    </Section>
                  );
                }

                // Warzone BR squads: positions are SQUAD placements, so TIES
                // are legal - every squadmate shares one position. Mirrors the
                // adminResolveRace server rule: contiguous 1..K, each position
                // used 1..squadSize times (partial squads legal). Solos and
                // ordinary races keep the strict each-position-once path.
                const brSquadSize = Math.max(1, Number(eventData.squadSize) || 1);
                const allowTies = eventData.battleRoyale === true && brSquadSize > 1;
                // Gold highlight: the doc stamps payoutStructure as an ID
                // string - look it up (wz-* falls back to the local mirror);
                // an unknown id just skips the highlight.
                const psRaw = eventData.payoutStructure;
                const psId = String(eventData.payoutStructureId
                  || (typeof psRaw === 'string' ? psRaw : '') || '');
                const psObj = (psRaw && typeof psRaw === 'object')
                  ? psRaw
                  : (PAYOUT_STRUCTURES[psId] || WZ_PAYOUT_STRUCTURES[psId] || null);
                const payingRanks = computePayingRanks(psObj);
                const finisherCount = allEntries.filter((e) => !raceDnf[e.id]).length;
                const positions = allEntries.filter((e) => !raceDnf[e.id]).map((e) => Number(racePositions[e.id]));
                const allFilled = positions.every((p) => Number.isInteger(p) && p >= 1);
                const sorted = [...positions].sort((a, b) => a - b);
                const contiguous = allFilled && (allowTies
                  ? (() => {
                      const counts = new Map();
                      positions.forEach((p) => counts.set(p, (counts.get(p) || 0) + 1));
                      const k = counts.size;
                      for (let r = 1; r <= k; r++) {
                        const c = counts.get(r) || 0;
                        if (c < 1 || c > brSquadSize) return false;
                      }
                      return true;
                    })()
                  : sorted.every((p, i) => p === i + 1));
                const alreadyCompleted = evidence?.eventData?.status === 'completed';
                const canSubmit = !alreadyCompleted && finisherCount > 0 && contiguous && reasonOk
                  && resolveConfirm === 'RESOLVE' && !resolving;

                return (
                  <Section title="⚖️ Resolve Race (Admin)">
                    <Text style={[styles.noDataText, { marginBottom: 14, textAlign: 'left' }]}>
                      Sets the full finishing order on this racing event and completes it —
                      payouts distribute automatically through the standard pipeline.
                      Positions must run 1…{finisherCount || 'N'} with no gaps; racers who
                      didn't finish get DNF (no rank, no payout). All inputs are
                      re-validated server-side.
                    </Text>

                    {allowTies && (
                      <Text style={[styles.noDataText, { marginBottom: 14, textAlign: 'left', color: COLORS.GOLD }]}>
                        Squad event: enter each player's SQUAD placement -
                        squadmates share a position (each position can repeat up
                        to {brSquadSize} times). Positions still run 1..K with
                        no gaps.
                      </Text>
                    )}

                    {alreadyCompleted && (
                      <View style={[styles.errorBox, { marginBottom: 12 }]}>
                        <Text style={styles.errorText}>
                          This event is already completed{evidence?.eventData?.championId ? ` (champion: ${evidence.eventData.championId})` : ''} —
                          payouts have run and the finishing order can't be changed here.
                        </Text>
                      </View>
                    )}

                    {allEntries.map((e) => {
                      const dnf = !!raceDnf[e.id];
                      const pos = racePositions[e.id] || '';
                      const paying = !dnf && payingRanks.size > 0 && payingRanks.has(Number(pos));
                      return (
                        <View key={e.id} style={{
                          flexDirection: 'row', alignItems: 'center', gap: 10,
                          backgroundColor: COLORS.CARD, borderWidth: 1, borderRadius: 8,
                          borderColor: paying ? COLORS.GOLD : COLORS.BORDER,
                          padding: 10, marginBottom: 8,
                        }}>
                          <TextInput
                            style={[styles.input, {
                              width: 58, marginBottom: 0, textAlign: 'center', opacity: dnf ? 0.3 : 1,
                              borderColor: paying ? COLORS.GOLD : COLORS.BORDER,
                              color: paying ? COLORS.GOLD : COLORS.TEXT,
                            }]}
                            value={dnf ? '—' : pos}
                            editable={!dnf && !alreadyCompleted}
                            onChangeText={(t) => setRacePositions((prev) => ({ ...prev, [e.id]: t.replace(/[^0-9]/g, '') }))}
                            placeholder="P#"
                            placeholderTextColor={COLORS.DIM}
                            keyboardType="numeric"
                          />
                          <View style={{ flex: 1 }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                              <Text style={{ color: COLORS.TEXT, fontSize: 14, fontWeight: '600' }}>
                                {e.ign || e.displayName || e.id}
                              </Text>
                              {e.ign && (
                                <Text style={{
                                  fontSize: 9, fontWeight: '700', letterSpacing: 1, color: COLORS.GOLD,
                                  borderWidth: 1, borderColor: COLORS.GOLD, borderRadius: 4,
                                  paddingHorizontal: 5, paddingVertical: 1,
                                }}>{serviceTag}</Text>
                              )}
                              {paying && (
                                <Text style={{
                                  fontSize: 9, fontWeight: '700', letterSpacing: 1, color: '#000',
                                  backgroundColor: COLORS.GOLD, borderRadius: 4,
                                  paddingHorizontal: 5, paddingVertical: 1,
                                }}>PAYOUT</Text>
                              )}
                            </View>
                            <Text selectable style={{ fontFamily: 'monospace', fontSize: 10, color: COLORS.DIM, marginTop: 2 }}>
                              {e.id}
                            </Text>
                            {e.visionOutcome && (
                              <Text style={{ fontSize: 10, color: COLORS.DIM, marginTop: 2 }}>
                                vision: {e.visionOutcome.toUpperCase()}{e.visionPattern ? ` · ${e.visionPattern}` : ''}
                              </Text>
                            )}
                          </View>
                          <TouchableOpacity
                            disabled={alreadyCompleted}
                            style={{
                              borderWidth: 1, borderRadius: 999, paddingHorizontal: 10, paddingVertical: 4,
                              borderColor: dnf ? COLORS.DANGER : COLORS.BORDER,
                            }}
                            onPress={() => setRaceDnf((prev) => ({ ...prev, [e.id]: !prev[e.id] }))}
                          >
                            <Text style={{ fontSize: 11, fontWeight: '700', color: dnf ? COLORS.DANGER : COLORS.DIM }}>DNF</Text>
                          </TouchableOpacity>
                        </View>
                      );
                    })}

                    {!contiguous && finisherCount > 0 && Object.keys(racePositions).length > 0 && (
                      <Text style={[styles.errorText, { marginBottom: 8 }]}>
                        {allowTies
                          ? `Squad positions must be contiguous from 1 with no gaps; each position may repeat at most ${brSquadSize} times.`
                          : `Positions must be exactly 1…${finisherCount} with no duplicates or gaps.`}
                      </Text>
                    )}

                    {renderReasonConfirmSubmit(canSubmit, submitRaceResolution, 'Resolve Race')}
                  </Section>
                );
              }

              // ---- MATCH PANEL (per-game variants) ---------------------------
              const scoreUi = scoreUiFor(gameKey, evidence?.eventData);

              return (
                <Section title="⚖️ Resolve Match (Admin)">
                  <Text style={[styles.noDataText, { marginBottom: 14, textAlign: 'left' }]}>
                    Sets the winner and score through the standard completion pipeline
                    (bracket, notifications, payouts) with a full audit record. All inputs
                    are re-validated server-side.
                  </Text>

                  {/* 1. Pick the match */}
                  {matches.map((m) => {
                    const isSelected = resolveMatchId === m.id;
                    const sideLabel = (p) => p?.ign || p?.displayName || p?.userId || '?';
                    return (
                      <TouchableOpacity
                        key={m.id}
                        style={[styles.recordingCard, isSelected && { borderColor: COLORS.GOLD, borderWidth: 1 }]}
                        onPress={() => {
                          setResolveMatchId(isSelected ? null : m.id);
                          setResolveWinner(null);
                          setSetScorePick(null);
                          setGt7ExitTag(null);
                          setResolveResult(null);
                        }}
                      >
                        <View style={styles.recordingInfo}>
                          <Text style={styles.recordingFilename}>
                            {m.id} — {sideLabel(m.p1)} vs {sideLabel(m.p2)}
                          </Text>
                          <Text style={styles.recordingMeta}>
                            status: {m.status || '?'}{m.winnerId ? ` • winner: ${m.winnerId}` : ' • no winner'}
                            {m.isTeamMatch ? ' • team match' : ''}
                            {m.adminResolution ? ' • previously admin-resolved' : ''}
                          </Text>
                        </View>
                        <Text style={styles.recordingPlay}>{isSelected ? '▼' : '▶'}</Text>
                      </TouchableOpacity>
                    );
                  })}

                  {/* 2. Resolution form for the selected match */}
                  {resolveMatchId && (() => {
                    const m = matches.find((x) => x.id === resolveMatchId);
                    if (!m) return null;
                    const sides = [m.p1, m.p2].filter((p) => p && (p.userId || p.teamId || p.pickupGroupId || (p.roster || []).length));
                    const alreadyCompleted = m.status === 'completed' && m.winnerId;
                    const scoresProvided = resolveWinnerScore !== '' || resolveLoserScore !== '';
                    const scoresOk = !scoresProvided
                      || (Number(resolveWinnerScore) > Number(resolveLoserScore)
                          && Number.isInteger(Number(resolveWinnerScore)) && Number.isInteger(Number(resolveLoserScore)));
                    const canSubmit = resolveWinner && reasonOk && scoresOk
                      && resolveConfirm === 'RESOLVE' && (!alreadyCompleted || resolveForce) && !resolving;

                    const winnerSide = sides.find((p) => sideWinnerId(p) === resolveWinner) || null;
                    const loserSide = winnerSide ? sides.find((p) => p !== winnerSide) : null;

                    // GT7 H2H loser exit-type chips — prefill the audit reason.
                    const exitTags = ['Finished P2', 'DNF', 'Rage quit', 'Disconnected'];
                    const applyExitTag = (tag) => {
                      setGt7ExitTag(tag);
                      const loserName = loserSide?.ign || loserSide?.displayName || 'Opponent';
                      setResolveReason(`${loserName}: ${tag.toLowerCase()} — `);
                    };

                    // Set-score picker (SF6 + fighting games): winner's number is
                    // fixed by the format, so offer only possible set scores.
                    const setOptions = scoreUi.kind === 'set'
                      ? Array.from({ length: scoreUi.firstTo }, (_, i) => `${scoreUi.firstTo}-${i}`)
                      : [];
                    const applySetPick = (opt) => {
                      setSetScorePick(opt);
                      if (opt === 'other') {
                        setResolveWinnerScore('');
                        setResolveLoserScore('');
                      } else {
                        const [w, l] = opt.split('-');
                        setResolveWinnerScore(w);
                        setResolveLoserScore(l);
                      }
                    };

                    const stripUids = sides.flatMap((p) => (
                      p.isTeam ? (p.roster || []).map((r) => r.userId) : [p.userId]
                    )).filter(Boolean);

                    return (
                      <View style={{ marginTop: 14 }}>
                        {renderVisionStrip(stripUids)}

                        <Text style={styles.recordingFilename}>{m.isTeamMatch ? 'Winning team' : 'Winner'}</Text>
                        <View style={{ flexDirection: m.isTeamMatch ? 'column' : 'row', gap: 10, marginVertical: 10 }}>
                          {sides.map((p, idx) => {
                            const wid = sideWinnerId(p);
                            const selected = resolveWinner === wid;
                            return (
                              <TouchableOpacity
                                key={wid || idx}
                                style={[styles.searchBtn, {
                                  flex: m.isTeamMatch ? undefined : 1,
                                  alignItems: 'flex-start',
                                  backgroundColor: selected ? COLORS.SUCCESS : COLORS.CARD,
                                  borderWidth: 1,
                                  borderColor: selected ? COLORS.SUCCESS : COLORS.BORDER,
                                }]}
                                onPress={() => setResolveWinner(wid)}
                              >
                                {p.isTeam ? (
                                  <View style={{ width: '100%' }}>
                                    <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                                      <Text style={[styles.searchBtnText, { color: selected ? '#000' : COLORS.TEXT, fontSize: 15, textAlign: 'left' }]}>
                                        🏆 {p.displayName || 'Team'}
                                      </Text>
                                      <Text style={{
                                        fontSize: 9, fontWeight: '700', letterSpacing: 1,
                                        color: selected ? '#05400f' : COLORS.GOLD,
                                        borderWidth: 1, borderColor: selected ? '#05400f' : COLORS.GOLD,
                                        borderRadius: 4, paddingHorizontal: 5, paddingVertical: 1,
                                      }}>P{idx + 1} · {(p.roster || []).length} PLAYERS</Text>
                                    </View>
                                    {renderRoster(p, selected)}
                                  </View>
                                ) : renderIdentity(p, selected)}
                              </TouchableOpacity>
                            );
                          })}
                        </View>

                        {/* Score inputs — semantics per game */}
                        {scoreUi.kind === 'points' && (
                          <View>
                            <Text style={styles.recordingFilename}>{scoreUi.label} (optional, winner first)</Text>
                            <View style={{ flexDirection: 'row', gap: 10, marginVertical: 10 }}>
                              <TextInput
                                style={[styles.input, { flex: 1, marginBottom: 0 }]}
                                value={resolveWinnerScore}
                                onChangeText={setResolveWinnerScore}
                                placeholder="Winner"
                                placeholderTextColor={COLORS.DIM}
                                keyboardType="numeric"
                              />
                              <TextInput
                                style={[styles.input, { flex: 1, marginBottom: 0 }]}
                                value={resolveLoserScore}
                                onChangeText={setResolveLoserScore}
                                placeholder="Loser"
                                placeholderTextColor={COLORS.DIM}
                                keyboardType="numeric"
                              />
                            </View>
                            {scoreUi.hint && (
                              <Text style={{ fontSize: 11, color: COLORS.DIM, marginBottom: 8 }}>{scoreUi.hint}</Text>
                            )}
                          </View>
                        )}

                        {scoreUi.kind === 'set' && (
                          <View>
                            <Text style={styles.recordingFilename}>{scoreUi.label}</Text>
                            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginVertical: 10 }}>
                              {[...setOptions, 'other'].map((opt) => {
                                const on = setScorePick === opt;
                                return (
                                  <TouchableOpacity
                                    key={opt}
                                    style={{
                                      borderWidth: 1, borderRadius: 999, paddingHorizontal: 12, paddingVertical: 5,
                                      borderColor: on ? COLORS.GOLD : COLORS.BORDER,
                                      backgroundColor: COLORS.CARD,
                                    }}
                                    onPress={() => applySetPick(opt)}
                                  >
                                    <Text style={{ fontSize: 12, fontWeight: '600', color: on ? COLORS.GOLD : COLORS.DIM }}>
                                      {opt === 'other' ? 'Other / no score' : opt.replace('-', '–')}
                                    </Text>
                                  </TouchableOpacity>
                                );
                              })}
                            </View>
                            {setScorePick === 'other' && (
                              <View style={{ flexDirection: 'row', gap: 10, marginBottom: 10 }}>
                                <TextInput
                                  style={[styles.input, { flex: 1, marginBottom: 0 }]}
                                  value={resolveWinnerScore}
                                  onChangeText={setResolveWinnerScore}
                                  placeholder="Winner (optional)"
                                  placeholderTextColor={COLORS.DIM}
                                  keyboardType="numeric"
                                />
                                <TextInput
                                  style={[styles.input, { flex: 1, marginBottom: 0 }]}
                                  value={resolveLoserScore}
                                  onChangeText={setResolveLoserScore}
                                  placeholder="Loser (optional)"
                                  placeholderTextColor={COLORS.DIM}
                                  keyboardType="numeric"
                                />
                              </View>
                            )}
                          </View>
                        )}

                        {scoreUi.kind === 'none' && gameKey === 'gt7' && (
                          <View>
                            <Text style={styles.recordingFilename}>How did the loser lose? (prefills the audit reason)</Text>
                            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginVertical: 10 }}>
                              {exitTags.map((tag) => {
                                const on = gt7ExitTag === tag;
                                return (
                                  <TouchableOpacity
                                    key={tag}
                                    style={{
                                      borderWidth: 1, borderRadius: 999, paddingHorizontal: 12, paddingVertical: 5,
                                      borderColor: on ? COLORS.GOLD : COLORS.BORDER,
                                      backgroundColor: COLORS.CARD,
                                    }}
                                    onPress={() => applyExitTag(tag)}
                                  >
                                    <Text style={{ fontSize: 12, fontWeight: '600', color: on ? COLORS.GOLD : COLORS.DIM }}>{tag}</Text>
                                  </TouchableOpacity>
                                );
                              })}
                            </View>
                          </View>
                        )}

                        {scoresProvided && !scoresOk && (
                          <Text style={[styles.errorText, { marginBottom: 8 }]}>
                            Scores must be whole numbers and the winner's score must be higher.
                          </Text>
                        )}

                        {alreadyCompleted && (
                          <TouchableOpacity
                            style={[styles.errorBox, { marginTop: 4, marginBottom: 8 }]}
                            onPress={() => setResolveForce(!resolveForce)}
                          >
                            <Text style={styles.errorText}>
                              {resolveForce ? '☑' : '☐'} This match is already completed (winner: {m.winnerId}).
                              Tap to confirm you want to OVERRIDE the existing result.
                            </Text>
                          </TouchableOpacity>
                        )}

                        {renderReasonConfirmSubmit(canSubmit, submitResolution, 'Resolve Match')}
                      </View>
                    );
                  })()}
                </Section>
              );
            })()}
          </View>
        )}
      </ScrollView>

      {/* Frame Modal */}
      {selectedFrame && (
        <Modal
          visible={true}
          transparent={true}
          animationType="fade"
          onRequestClose={() => setSelectedFrame(null)}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              <TouchableOpacity
                style={styles.closeBtn}
                onPress={() => setSelectedFrame(null)}
              >
                <Text style={styles.closeBtnText}>✕</Text>
              </TouchableOpacity>

              <Text style={styles.modalTitle}>
                Frame Details
              </Text>

              <ScrollView style={styles.modalScroll}>
                <Image
                  source={{ uri: selectedFrame.frame.url }}
                  style={styles.frameFullImage}
                  resizeMode="contain"
                />
                <Text style={styles.frameDetailText}>
                  Filename: {selectedFrame.frame.filename}
                </Text>
                <Text style={styles.frameDetailText}>
                  User: {selectedFrame.frame.userId}
                </Text>
                <Text style={styles.frameDetailText}>
                  Path: {selectedFrame.frame.fullPath}
                </Text>
              </ScrollView>
            </View>
          </View>
        </Modal>
      )}
      {/* Video Player Modal */}
        {selectedRecording && (
          <Modal
            visible={true}
            transparent={true}
            animationType="fade"
            onRequestClose={() => setSelectedRecording(null)}
          >
            <View style={styles.modalOverlay}>
              <View style={styles.videoModalContent}>
                <TouchableOpacity
                  style={styles.closeBtn}
                  onPress={() => setSelectedRecording(null)}
                >
                  <Text style={styles.closeBtnText}>✕</Text>
                </TouchableOpacity>

                <Text style={styles.modalTitle}>Recording Playback</Text>

                <View style={styles.videoContainer}>
                  <HlsVideoPlayer url={selectedRecording.url} />
                </View>

                <Text style={styles.frameDetailText}>
                  {selectedRecording.displayName || selectedRecording.filename}
                </Text>
                <Text style={styles.frameDetailText}>
                  {selectedRecording.source === 'mux'
                    ? `Duration: ${selectedRecording.duration ? `${Math.round(selectedRecording.duration)}s` : 'Unknown'}`
                    : `Size: ${formatBytes(selectedRecording.size)}`
                  }
                </Text>
                <Text style={styles.frameDetailText}>
                  Created: {selectedRecording.created ? new Date(selectedRecording.created).toLocaleString() : 'Unknown'}
                </Text>
                {selectedRecording.source === 'mux' && (
                  <Text style={styles.frameDetailText}>
                    Source: Mux VOD {selectedRecording.mp4Ready ? '(MP4 available)' : ''}
                  </Text>
                )}
              </View>
            </View>
          </Modal>
        )}

      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );
}

// HLS-aware video player — handles both .m3u8 (Mux VOD) and direct .mp4 URLs
const HlsVideoPlayer = ({ url }) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !url) return;

    // Clean up previous HLS instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    const isHls = url.includes('.m3u8');

    if (isHls && window.Hls && window.Hls.isSupported()) {
      const hls = new window.Hls();
      hlsRef.current = hls;
      hls.loadSource(url);
      hls.attachMedia(video);
      hls.on(window.Hls.Events.MANIFEST_PARSED, () => {
        video.play().catch(() => {});
      });
    } else if (isHls && video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS
      video.src = url;
      video.play().catch(() => {});
    } else {
      // Direct MP4 or other format
      video.src = url;
      video.play().catch(() => {});
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [url]);

  // Load HLS.js from CDN if not already available
  useEffect(() => {
    if (window.Hls) return;
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/hls.js@latest';
    script.async = true;
    document.head.appendChild(script);
  }, []);

  return (
    <video
      ref={videoRef}
      controls
      style={{ width: '100%', maxHeight: '60vh', backgroundColor: '#000', borderRadius: 8 }}
    />
  );
};

// Helper Components
const Section = ({ title, children }) => (
  <View style={styles.section}>
    <Text style={styles.sectionTitle}>{title}</Text>
    {children}
  </View>
);

const InfoRow = ({ label, value }) => (
  <View style={styles.infoRow}>
    <Text style={styles.infoLabel}>{label}:</Text>
    <Text style={styles.infoValue}>{value}</Text>
  </View>
);

// Styles
const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: COLORS.BG },
  container: { padding: 16, paddingBottom: 120 },
  centerWrap: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 },
  loadingWrap: { alignItems: 'center', padding: 40 },
  deniedText: { color: COLORS.DANGER, fontSize: 18, fontWeight: 'bold', textAlign: 'center' },
  backLink: { color: COLORS.DIM, marginTop: 16, textDecorationLine: 'underline' },
  
  header: { alignItems: 'center', marginBottom: 20 },
  title: { fontSize: 26, fontWeight: '900', color: COLORS.GOLD, textAlign: 'center' },
  subtitle: { color: COLORS.DIM, fontSize: 13, marginTop: 4, textAlign: 'center' },

  // Toggle
  toggleContainer: {
    flexDirection: 'row',
    backgroundColor: COLORS.CARD,
    borderRadius: 8,
    padding: 4,
    marginBottom: 16,
  },
  toggleBtn: {
    flex: 1,
    paddingVertical: 10,
    alignItems: 'center',
    borderRadius: 6,
  },
  toggleBtnActive: {
    backgroundColor: COLORS.GOLD,
  },
  toggleText: {
    color: COLORS.DIM,
    fontSize: 14,
    fontWeight: '600',
  },
  toggleTextActive: {
    color: '#000',
  },

  // Search
  searchSection: { marginBottom: 20 },
  input: {
    backgroundColor: COLORS.CARD,
    borderWidth: 1,
    borderColor: COLORS.BORDER,
    borderRadius: 8,
    padding: 12,
    color: COLORS.TEXT,
    fontSize: 14,
    marginBottom: 12,
  },
  searchBtn: {
    backgroundColor: COLORS.GOLD,
    padding: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  searchBtnText: { color: '#000', fontSize: 16, fontWeight: 'bold' },

  // Error
  errorBox: {
    backgroundColor: COLORS.DANGER + '20',
    borderWidth: 1,
    borderColor: COLORS.DANGER,
    borderRadius: 8,
    padding: 12,
    marginBottom: 20,
  },
  errorText: { color: COLORS.DANGER, fontSize: 14 },

  loadingText: { color: COLORS.DIM, marginTop: 12, fontSize: 14 },

  // Tab Bar
  tabBar: {
    flexDirection: 'row',
    backgroundColor: COLORS.CARD,
    borderRadius: 8,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: COLORS.BORDER,
  },
  tab: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
  },
  tabActive: {
    borderBottomWidth: 2,
    borderBottomColor: COLORS.GOLD,
  },
  tabText: {
    color: COLORS.DIM,
    fontSize: 13,
    fontWeight: '600',
  },
  tabTextActive: {
    color: COLORS.GOLD,
  },

  // Results
  resultsSection: { width: '100%' },

  // Section
  section: { width: '100%', marginBottom: 24 },
  sectionTitle: {
    color: COLORS.TEXT,
    fontSize: 15,
    fontWeight: 'bold',
    marginBottom: 12,
    borderLeftWidth: 3,
    borderLeftColor: COLORS.GOLD,
    paddingLeft: 8,
  },

  // Info Row
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.BORDER,
  },
  infoLabel: { color: COLORS.DIM, fontSize: 13 },
  infoValue: { color: COLORS.TEXT, fontSize: 13, fontWeight: '600', maxWidth: '60%', textAlign: 'right' },

  // Event Card
  eventCard: {
    backgroundColor: COLORS.CARD,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.BORDER,
    padding: 14,
    marginBottom: 12,
  },
  eventCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  eventCardTitle: {
    color: COLORS.TEXT,
    fontSize: 16,
    fontWeight: 'bold',
  },
  visionBadge: {
    backgroundColor: COLORS.PURPLE + '30',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  visionBadgeText: {
    color: COLORS.PURPLE,
    fontSize: 11,
    fontWeight: '600',
  },
  eventCardId: {
    color: COLORS.DIM,
    fontSize: 11,
    fontFamily: 'monospace',
    marginBottom: 8,
  },
  eventCardRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  eventCardLabel: {
    color: COLORS.DIM,
    fontSize: 12,
  },
  eventCardValue: {
    color: COLORS.TEXT,
    fontSize: 12,
    fontWeight: '600',
  },
  eventCardDate: {
    color: COLORS.DIM,
    fontSize: 11,
    marginTop: 8,
  },

  // Entry Evidence Card
  entryCard: {
    backgroundColor: COLORS.CARD,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.BORDER,
    padding: 14,
    marginBottom: 12,
  },
  entryTitle: {
    color: COLORS.GOLD,
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 12,
  },
  entryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  entryLabel: {
    color: COLORS.DIM,
    fontSize: 12,
  },
  entryValue: {
    color: COLORS.TEXT,
    fontSize: 12,
    fontWeight: '600',
    maxWidth: '60%',
    textAlign: 'right',
  },

  // Frames
  framesRow: {
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  frameCard: {
    width: '48%',
    backgroundColor: COLORS.CARD,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.BORDER,
    overflow: 'hidden',
  },
  frameThumbnail: {
    width: '100%',
    height: 100,
    backgroundColor: COLORS.BORDER,
  },
  frameInfo: {
    padding: 8,
  },
  frameFilename: {
    color: COLORS.TEXT,
    fontSize: 10,
    fontFamily: 'monospace',
  },
  frameUser: {
    color: COLORS.DIM,
    fontSize: 9,
    marginTop: 2,
  },
  frameOutcome: {
    fontSize: 10,
    fontWeight: '600',
    marginTop: 2,
  },
  frameError: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: COLORS.DANGER + '20',
  },
  frameErrorText: {
    fontSize: 24,
  },
  frameErrorLabel: {
    color: COLORS.DANGER,
    fontSize: 10,
    marginTop: 4,
  },

  noDataText: {
    color: COLORS.DIM,
    fontSize: 14,
    textAlign: 'center',
    padding: 20,
  },

  // Mini Entry Card (for All Entries list)
  miniEntryCard: {
    backgroundColor: COLORS.CARD,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: COLORS.BORDER,
    padding: 10,
    marginBottom: 8,
  },
  miniEntryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  miniEntryName: {
    color: COLORS.TEXT,
    fontSize: 14,
    fontWeight: '600',
  },
  miniEntryStatus: {
    fontSize: 11,
    fontWeight: '600',
  },
  miniEntryId: {
    color: COLORS.DIM,
    fontSize: 10,
    fontFamily: 'monospace',
    marginTop: 2,
  },
  miniEntryStreamKey: {
    color: COLORS.BLUE,
    fontSize: 10,
    fontFamily: 'monospace',
    marginTop: 2,
  },

  // Recording Card
  recordingCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.CARD,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.BORDER,
    padding: 12,
    marginBottom: 10,
  },
  recordingIcon: {
    width: 50,
    height: 50,
    borderRadius: 8,
    backgroundColor: COLORS.BLUE + '20',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  recordingIconText: {
    fontSize: 24,
  },
  recordingInfo: {
    flex: 1,
  },
  recordingFilename: {
    color: COLORS.TEXT,
    fontSize: 13,
    fontWeight: '600',
    marginBottom: 2,
  },
  recordingMeta: {
    color: COLORS.DIM,
    fontSize: 11,
    marginBottom: 2,
  },
  recordingStreamKey: {
    color: COLORS.BLUE,
    fontSize: 9,
    fontFamily: 'monospace',
  },
  recordingPlay: {
    color: COLORS.GOLD,
    fontSize: 20,
    marginLeft: 8,
  },
  clipBadge: {
    backgroundColor: '#2ECC71',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  clipBadgeText: {
    color: '#000',
    fontSize: 9,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  
  frameSize: {
    color: COLORS.DIM,
    fontSize: 9,
    marginTop: 2,
  },

  // Modal
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.9)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    width: '90%',
    maxHeight: '85%',
    backgroundColor: COLORS.CARD,
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    borderColor: COLORS.BORDER,
  },
  closeBtn: {
    position: 'absolute',
    top: 10,
    right: 10,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: COLORS.DANGER,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  closeBtnText: { color: '#FFF', fontSize: 20, fontWeight: 'bold' },
  modalTitle: {
    color: COLORS.GOLD,
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 16,
    textAlign: 'center',
  },
  modalScroll: { width: '100%' },
  frameFullImage: {
    width: '100%',
    height: 300,
    backgroundColor: COLORS.BG,
    marginBottom: 16,
  },
  frameDetailText: {
    color: COLORS.TEXT,
    fontSize: 12,
    marginBottom: 8,
    fontFamily: 'monospace',
  },
  videoModalContent: {
    width: '95%',
    maxWidth: 900,
    maxHeight: '90%',
    backgroundColor: COLORS.CARD,
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    borderColor: COLORS.BORDER,
  },
  videoContainer: {
    width: '100%',
    backgroundColor: '#000',
    borderRadius: 8,
    overflow: 'hidden',
    marginBottom: 16,
  },
});